import { BadgeSelect, Button, Icon } from "shared";
import {
    ComponentTypes,
    ITemplateComponent,
    ITemplateCheckboxComponent,
    ITemplateRadioComponent,
    ITemplateSelectComponent,
    ITemplateInputWithSelectComponent,
    ITemplateTableComponent,
    ITemplateMultilineComponent,
    ITemplateDateInputComponent,
    ITemplateFileComponent,
    ITemplateHrPersonComponent,
    ITemplateInputComponent,
    ITemplateMoneyComponent,
    ITemplateTextComponent,
    ITemplateYandexComponent,
    ITemplateCounterpartyComponent,
    ITemplateLanguageComponent,
    ITemplateExternalFormComponent,
    ITemplateUuidComponent,
    ITemplateBarcodeComponent,
    ITemplateMassGenerationComponent,
    ITemplateSaveAndTicketComponent,
    ITemplateSaveToExistingTicketComponent,
    ITemplateTrackerIntegrationComponent,
    ITemplateProcureComponent,
    ITemplatePdfPasswordComponent,
    ITemplateAutonumberComponent
} from "entities/template";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { defaultComponentValue } from "../../lib/utils/default_component_value";
import { useComponentsConfig } from "../../lib/hooks/useComponentsConfig";
import { ComponentFormText } from "./forms/component_form_text/component_form_text";
import { ComponentFormInput } from "./forms/component_form_input/component_form_input";
import { ComponentFormMoney } from "./forms/component_form_money/component_form_money";
import { ComponentFormCheckbox } from "./forms/component_form_checkbox/component_form_checkbox";
import { ComponentFormRadio } from "./forms/component_form_radio/component_form_radio";
import { ComponentFormSelect } from "./forms/component_form_select/component_form_select";
import {
    ComponentFormInputWithSelect
} from "./forms/component_form_input_with_select/component_form_input_with_select";
import { ComponentFormTable } from "./forms/component_form_table/component_form_table";
import { ComponentFormMultiline } from "./forms/component_form_multiline/component_form_multiline";
import { ComponentFormDateInput } from "./forms/component_form_date_input/component_form_date_input";
import { ComponentFormEmployee } from "./forms/component_form_employee/component_form_employee";
import { ComponentFormFile } from "./forms/component_form_file/component_form_file";
import { ComponentFormCounterparty } from "./forms/component_form_counterparty/component_form_counterparty";
import { ComponentFormYandex } from "./forms/component_form_yandex/component_form_yandex";
import { ComponentFormLegalYandex } from "./forms/component_form_legal_yandex/component_form_legal_yandex";
import { ComponentExternalForm } from "./forms/component_external_form/component_external_form";
import { ComponentFormLanguage } from "./forms/component_form_language/component_form_language";
import { ComponentFormPreview } from "./forms/component_form_preview/component_form_preview";
import { ComponentFormUuid } from "./forms/component_form_uuid/component_form_uuid";
import { ComponentFormBarcode } from "./forms/component_form_barcode/component_form_barcode";
import { ComponentFormMassGeneration } from "./forms/component_form_massgeneration/component_form_massgeneration";
import { ComponentFormSaveAndTicket } from "./forms/component_form_save_and_ticket/component_form_save_and_ticket";
import {
    ComponentFormSaveToExistingTicket
} from "./forms/component_form_save_to_existing_ticket/component_form_save_to_existing_ticket";
import {
    ComponentFormTrackerIntegration
} from "./forms/component_form_tracker_integration/component_form_tracker_integration";
import { ComponentFormProcure } from "./forms/component_form_procure/component_form_procure";
import { ComponentFormPdfPassword } from "./forms/component_form_pdf_password/component_form_pdf_password";
import { ComponentFormAutonumber } from "./forms/component_form_autonumber/component_form_autonumber";
import styles from "./component_form.module.scss";


interface IComponentFormProps {
    component: ITemplateComponent;
    actionPanel?: React.ReactNode;
    componentList: ITemplateComponent[];
    onChange: (component: ITemplateComponent) => void;
    onTemplatePasswordChange: (value?: string) => void;
    triggerValidate: number;
    templatePassword?: string;
    onBackButtonClick: () => void
}

export const ComponentForm: React.FC<IComponentFormProps> = React.memo(({
                                                                            component,
                                                                            componentList,
                                                                            actionPanel,
                                                                            onTemplatePasswordChange,
                                                                            templatePassword,
                                                                            triggerValidate,
                                                                            onChange,
                                                                            onBackButtonClick
                                                                        }) => {
    const componentsConfig = useComponentsConfig();
    const { i18n } = useTranslation();
    const componentTypeOptions = useMemo(() => {
        return Object.keys(componentsConfig).map(key => {
            return {
                value: key,
                label: componentsConfig[key as ComponentTypes].label
            }
        })
    }, [componentsConfig, i18n.language])

    const enLanguageEnabled = useMemo(() => {
        const langComponent = componentList.find(c => c.type === ComponentTypes.language) as ITemplateLanguageComponent | undefined;
        return !!langComponent && langComponent.isLangEn;
    }, [componentList])

    return <div className={styles.container}>
        <div className={styles.headerGroup}>
            <Button onClick={onBackButtonClick}><Icon glyph="arrowShortLeft" size="l"/></Button>
            <BadgeSelect
                onChange={(newComponentType) => {
                    const defaultComponent = defaultComponentValue(component.id as number, newComponentType as ComponentTypes)
                    onChange({...component, ...defaultComponent, label: component.label ?? "", label_en: component.label_en ?? "" })
                }}
                value={component.type}
                excludedOptionKeys={[ComponentTypes.yandex]}
                options={componentTypeOptions}
            />
            {actionPanel}
        </div>
        {component.type === ComponentTypes.text &&
            <ComponentFormText
                component={component as ITemplateTextComponent}
                enLanguageEnabled={enLanguageEnabled}
                componentList={componentList}
                onChange={onChange}/>}
        {component.type === ComponentTypes.input &&
            <ComponentFormInput
                component={component as ITemplateInputComponent}
                enLanguageEnabled={enLanguageEnabled}
                triggerValidate={triggerValidate}
                componentList={componentList}
                onChange={onChange}/>}
        {component.type === ComponentTypes.money &&
            <ComponentFormMoney
                component={component as ITemplateMoneyComponent}
                enLanguageEnabled={enLanguageEnabled}
                triggerValidate={triggerValidate}
                componentList={componentList}
                onChange={onChange}/>}
        {component.type === ComponentTypes.checkbox &&
            <ComponentFormCheckbox
                component={component as ITemplateCheckboxComponent}
                enLanguageEnabled={enLanguageEnabled}
                triggerValidate={triggerValidate}
                componentList={componentList}
                onChange={onChange}/>}
        {component.type === ComponentTypes.radio &&
            <ComponentFormRadio
                component={component as ITemplateRadioComponent}
                enLanguageEnabled={enLanguageEnabled}
                triggerValidate={triggerValidate}
                componentList={componentList}
                onChange={onChange}/>}
        {component.type === ComponentTypes.select &&
            <ComponentFormSelect
                component={component as ITemplateSelectComponent}
                enLanguageEnabled={enLanguageEnabled}
                triggerValidate={triggerValidate}
                componentList={componentList}
                onChange={onChange}/>}
        {component.type === ComponentTypes.inputWithSelect &&
            <ComponentFormInputWithSelect
                component={component as ITemplateInputWithSelectComponent}
                enLanguageEnabled={enLanguageEnabled}
                triggerValidate={triggerValidate}
                componentList={componentList}
                onChange={onChange}/>}
        {component.type === ComponentTypes.table &&
            <ComponentFormTable
                component={component as ITemplateTableComponent}
                enLanguageEnabled={enLanguageEnabled}
                triggerValidate={triggerValidate}
                componentList={componentList}
                onChange={onChange}/>}
        {component.type === ComponentTypes.multiline &&
            <ComponentFormMultiline
                component={component as ITemplateMultilineComponent}
                enLanguageEnabled={enLanguageEnabled}
                triggerValidate={triggerValidate}
                componentList={componentList}
                onChange={onChange}/>}
        {component.type === ComponentTypes.dateInput &&
            <ComponentFormDateInput
                component={component as ITemplateDateInputComponent}
                enLanguageEnabled={enLanguageEnabled}
                triggerValidate={triggerValidate}
                componentList={componentList}
                onChange={onChange}/>}
        {component.type === ComponentTypes.file &&
            <ComponentFormFile
                component={component as ITemplateFileComponent}
                enLanguageEnabled={enLanguageEnabled}
                triggerValidate={triggerValidate}
                componentList={componentList}
                onChange={onChange}/>}
        {component.type === ComponentTypes.hrPerson &&
            <ComponentFormEmployee
                component={component as ITemplateHrPersonComponent}
                enLanguageEnabled={enLanguageEnabled}
                triggerValidate={triggerValidate}
                componentList={componentList}
                onChange={onChange}/>}
        {component.type === ComponentTypes.counterparty &&
            <ComponentFormCounterparty
                component={component as ITemplateCounterpartyComponent}
                enLanguageEnabled={enLanguageEnabled}
                triggerValidate={triggerValidate}
                componentList={componentList}
                onChange={onChange}/>}
        {component.type === ComponentTypes.yandex &&
            <ComponentFormYandex
                component={component as ITemplateYandexComponent}
                enLanguageEnabled={enLanguageEnabled}
                triggerValidate={triggerValidate}
                componentList={componentList}
                onChange={onChange}/>}
        {component.type === ComponentTypes.legalYandex &&
            <ComponentFormLegalYandex
                component={component as ITemplateYandexComponent}
                enLanguageEnabled={enLanguageEnabled}
                triggerValidate={triggerValidate}
                componentList={componentList}
                onChange={onChange}/>}
        {component.type === ComponentTypes.language &&
            <ComponentFormLanguage
                component={component as ITemplateLanguageComponent}
                onChange={onChange}/>}
        {component.type === ComponentTypes.preview && <ComponentFormPreview/>}
        {component.type === ComponentTypes.uuid &&
            <ComponentFormUuid
                component={component as ITemplateUuidComponent}
                triggerValidate={triggerValidate}
                onChange={onChange}/>}
        {component.type === ComponentTypes.massDocGeneration &&
            <ComponentFormMassGeneration
                component={component as ITemplateMassGenerationComponent}
                triggerValidate={triggerValidate}
                onChange={onChange}/>}
        {component.type === ComponentTypes.barcode &&
            <ComponentFormBarcode
                component={component as ITemplateBarcodeComponent}
                enLanguageEnabled={enLanguageEnabled}
                triggerValidate={triggerValidate}
                componentList={componentList}
                onChange={onChange}/>}
        {component.type === ComponentTypes.pdfPassword &&
            <ComponentFormPdfPassword
                component={component as ITemplatePdfPasswordComponent}
                enLanguageEnabled={enLanguageEnabled}
                componentList={componentList}
                onChange={onChange}/>}
        {component.type === ComponentTypes.externalForm &&
            <ComponentExternalForm
                enLanguageEnabled={enLanguageEnabled}
                component={component as ITemplateExternalFormComponent}
                componentList={componentList}
                onChange={onChange}/>}
        {component.type === ComponentTypes.iprocIntegration &&
            <ComponentFormProcure
                component={component as ITemplateProcureComponent}
                enLanguageEnabled={enLanguageEnabled}
                triggerValidate={triggerValidate}
                componentList={componentList}
                onChange={onChange}/>}
        {component.type === ComponentTypes.saveAndTicket &&
            <ComponentFormSaveAndTicket
                component={component as ITemplateSaveAndTicketComponent}
                onTemplatePasswordChange={onTemplatePasswordChange}
                templatePassword={templatePassword}
                enLanguageEnabled={enLanguageEnabled}
                triggerValidate={triggerValidate}
                componentList={componentList}
                onChange={onChange}/>}
        {component.type === ComponentTypes.saveToExistingTicket &&
            <ComponentFormSaveToExistingTicket
                component={component as ITemplateSaveToExistingTicketComponent}
                enLanguageEnabled={enLanguageEnabled}
                componentList={componentList}
                triggerValidate={triggerValidate}
                onChange={onChange}/>}
        {component.type === ComponentTypes.trackerIntegration &&
            <ComponentFormTrackerIntegration
                component={component as ITemplateTrackerIntegrationComponent}
                triggerValidate={triggerValidate}
                componentList={componentList}
                onChange={onChange}/>}
        {component.type === ComponentTypes.autonumber &&
            <ComponentFormAutonumber
                component={component as ITemplateAutonumberComponent}
                componentList={componentList}
                triggerValidate={triggerValidate}
                onChange={onChange}/>}
    </div>;
});
